import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FmlxButton, FmlxTab } from 'fmlx-common-ui';
import ArchivingService from 'business/services/ArchivingService';
import { ResponseType } from 'business/models/Models';
import { useRMDispatch } from 'redux/hooks';
import {
  fetchActiveArchivingJobResultsThunk,
  fetchUndismissedArchivingJobResultsThunk,
} from 'redux/thunks/archiving';
import { PreviewTable } from './previewTable';
import { ResultsTable } from './resultsTable';
import {
  usePreviewArchiving,
  useIsArchivingJobActive,
  useAutoRefreshPreviewOnTreeSelection,
  useRemoveDeletedExperimentsFromPreview,
} from './hooks';
import { getArchivingStorageStatus } from './utils';

type PreviewProps = {
  selectedNodes: number[];
  selectedTab: number;
  onSelectedTabChange: (tab: number) => void;
  resultsJobId?: string | null;
};

export const Preview = (props: PreviewProps) => {
  const { selectedNodes, selectedTab, onSelectedTabChange, resultsJobId } = props;
  const [storageInfo, setStorageInfo] = useState<{
    availableSize: number;
    path: string;
  } | null>(null);
  const dispatch = useRMDispatch();
  const { onPreviewClick } = usePreviewArchiving();
  const isJobActive = useIsArchivingJobActive();
  const isRefreshDisabled = selectedTab === 0 && selectedNodes.length === 0;

  useAutoRefreshPreviewOnTreeSelection(selectedNodes, onPreviewClick);
  useRemoveDeletedExperimentsFromPreview();

  const handleStartOverComplete = useCallback(
    () => onPreviewClick(selectedNodes, false, true),
    [onPreviewClick, selectedNodes],
  );

  useEffect(() => {
    if (isJobActive) {
      onSelectedTabChange(1);
    }
  }, [isJobActive, onSelectedTabChange]);

  const onTabChange = (value: { index: number | string }) => {
    const parsed = typeof value.index === 'number' ? value.index : Number(value.index);
    if (Number.isNaN(parsed)) {
      return;
    }

    onSelectedTabChange(parsed);
  };

  useEffect(() => {
    fetchStorageData();
  }, []);

  const fetchStorageData = async () => {
    const response = await new ArchivingService().fetchArchivingLocation();
    if (response.responseType === ResponseType.Success && response.value) {
      setStorageInfo({
        availableSize: response.value.availableSize,
        path: response.value.location,
      });
    }
  };

  // Storage status is only evaluated once storage info has loaded. The notice is rendered
  // inside the preview table (above the rows); the Results tab has its own storage banner.
  const storageStatus = storageInfo
    ? getArchivingStorageStatus({
        location: storageInfo.path,
        availableSize: storageInfo.availableSize,
      })
    : 'ok';

  const items = useMemo(
    () => [
      {
        title: 'Preview',
        content: (
          <PreviewTable
            onStartOverComplete={handleStartOverComplete}
            storageStatus={storageStatus}
            hasSelection={selectedNodes.length > 0}
          />
        ),
        icon: undefined,
        disabled: false,
      },
      {
        title: 'Results',
        content: <ResultsTable />,
        icon: undefined,
        disabled: false,
      },
    ],
    [storageStatus, handleStartOverComplete, selectedNodes.length],
  );

  const onRefreshClick = () => {
    fetchStorageData();
    if (selectedTab === 0) {
      onPreviewClick(selectedNodes, false, true);
    } else if (resultsJobId) {
      dispatch(fetchActiveArchivingJobResultsThunk(resultsJobId));
    } else {
      dispatch(fetchUndismissedArchivingJobResultsThunk());
    }
  };

  return (
    <>
      <FmlxTab
        type={FmlxTab.Type.CONTAINED}
        items={items}
        selectedTab={selectedTab}
        variant={FmlxTab.Variant.STANDARD}
        onChange={onTabChange}
      />
      <div className="absolute right-0 m-2 flex flex-row items-center gap-2">
        <FmlxButton
          disabled={isRefreshDisabled}
          size="sm"
          label="Refresh"
          onClick={onRefreshClick}
        />
        <span>Archiving File Location: {storageInfo?.path}</span>
        <span>
          Free Space:{' '}
          {storageInfo && storageInfo.availableSize >= 0
            ? `${storageInfo.availableSize} GB`
            : 'N/A'}
        </span>
      </div>
    </>
  );
};
